// src/utils/mockDataAnalytics.ts
// Step 5 — Spend Analytics Dashboard
// Pre-aggregated spend data representing what a backend would compute
// from MOCK_POS, MOCK_INVOICES, MOCK_GRNS and MOCK_JOBS

// ── Types ─────────────────────────────────────────────────────────

export type SpendCategory =
  | 'POWER_CABLE' | 'COMMS_CABLE' | 'CONDUIT_FITTINGS'
  | 'SIGNAL_EQUIPMENT' | 'CIVIL_MATERIALS' | 'STEEL'
  | 'PPE' | 'CONSUMABLES' | 'FUEL' | 'SUBCONTRACT' | 'OTHER'

export interface MonthlySpend {
  month: string         // 'Jan 2026'
  isoMonth: string      // '2026-01'
  invoiced: number      // ex GST
  paid: number
  committed: number     // open POs (approved but not yet invoiced)
  gst: number
}

export interface CategorySpend {
  category: SpendCategory
  label: string
  color: string
  totalExGST: number
  invoiceCount: number
  pctOfTotal: number
  trend: 'UP' | 'DOWN' | 'STABLE'
  trendPct: number      // vs same period last quarter
}

export interface SupplierSpend {
  supplierId: string
  supplierName: string
  abn: string
  totalExGST: number
  invoiceCount: number
  lastInvoiceDate: string
  avgPaymentDays: number
  primaryCategory: SpendCategory
  hasContract: boolean
  ytdChangeVsLastYear: number   // %
}

export interface JobSpend {
  jobId: string
  jobNumber: string
  jobName: string
  contractValue: number
  totalCommitted: number        // open POs
  totalInvoiced: number
  totalPaid: number
  budgetUtilisationPct: number  // (invoiced / contractValue) * 100
  status: 'ON_BUDGET' | 'WARNING' | 'OVER_BUDGET'
}

export interface SpendKPI {
  label: string
  value: string
  sub: string
  trend?: number     // % vs prior period (positive = up)
  color: string
}

// ── Monthly spend trend (Jan–Jun 2026) ────────────────────────────

export const MONTHLY_SPEND: MonthlySpend[] = [
  { month: 'Jan 2026', isoMonth: '2026-01', invoiced: 28_400, paid: 22_800, committed: 84_200, gst: 2_840 },
  { month: 'Feb 2026', isoMonth: '2026-02', invoiced: 41_200, paid: 38_600, committed: 112_400, gst: 4_120 },
  { month: 'Mar 2026', isoMonth: '2026-03', invoiced: 68_800, paid: 61_200, committed: 96_800, gst: 6_880 },
  { month: 'Apr 2026', isoMonth: '2026-04', invoiced: 52_400, paid: 48_800, committed: 88_400, gst: 5_240 },
  { month: 'May 2026', isoMonth: '2026-05', invoiced: 34_100, paid: 29_600, committed: 74_200, gst: 3_410 },
  { month: 'Jun 2026', isoMonth: '2026-06', invoiced: 57_892, paid: 12_748, committed: 68_900, gst: 5_789 },
]

// ── Category spend breakdown ───────────────────────────────────────

export const CATEGORY_SPEND: CategorySpend[] = [
  { category: 'SIGNAL_EQUIPMENT', label: 'Signal equipment',    color: '#7C3AED', totalExGST: 95_840,  invoiceCount: 4,  pctOfTotal: 32.8, trend: 'STABLE', trendPct: 1.2  },
  { category: 'CIVIL_MATERIALS',  label: 'Civil materials',     color: '#0891B2', totalExGST: 72_400,  invoiceCount: 8,  pctOfTotal: 24.8, trend: 'UP',     trendPct: 8.4  },
  { category: 'POWER_CABLE',      label: 'Power cable',         color: '#1D4ED8', totalExGST: 47_120,  invoiceCount: 6,  pctOfTotal: 16.1, trend: 'UP',     trendPct: 14.2 },
  { category: 'SUBCONTRACT',      label: 'Subcontractors',      color: '#0F766E', totalExGST: 32_800,  invoiceCount: 3,  pctOfTotal: 11.2, trend: 'STABLE', trendPct: 0.8  },
  { category: 'CONDUIT_FITTINGS', label: 'Conduit & fittings',  color: '#059669', totalExGST: 18_640,  invoiceCount: 5,  pctOfTotal: 6.4,  trend: 'UP',     trendPct: 3.1  },
  { category: 'STEEL',            label: 'Steel & fixings',     color: '#6B7280', totalExGST: 12_480,  invoiceCount: 4,  pctOfTotal: 4.3,  trend: 'DOWN',   trendPct: -11.1},
  { category: 'PPE',              label: 'PPE & uniforms',      color: '#D97706', totalExGST: 8_204,   invoiceCount: 9,  pctOfTotal: 2.8,  trend: 'UP',     trendPct: 5.2  },
  { category: 'FUEL',             label: 'Fuel',                color: '#DC2626', totalExGST: 4_480,   invoiceCount: 4,  pctOfTotal: 1.5,  trend: 'DOWN',   trendPct: -9.2 },
  { category: 'CONSUMABLES',      label: 'Consumables & tools', color: '#4B5563', totalExGST: 1_928,   invoiceCount: 12, pctOfTotal: 0.7,  trend: 'STABLE', trendPct: 2.1  },
]

// ── Supplier spend breakdown ───────────────────────────────────────

export const SUPPLIER_SPEND: SupplierSpend[] = [
  { supplierId: 'sup-aldridge',   supplierName: 'Aldridge Traffic Architects',     abn: '42 007 135 844', totalExGST: 95_840,  invoiceCount: 4,  lastInvoiceDate: '2026-06-05', avgPaymentDays: 28, primaryCategory: 'SIGNAL_EQUIPMENT', hasContract: true,  ytdChangeVsLastYear: 4.2   },
  { supplierId: 'sup-boral',      supplierName: 'Boral Construction Materials',     abn: '12 004 796 249', totalExGST: 58_200,  invoiceCount: 7,  lastInvoiceDate: '2026-06-18', avgPaymentDays: 25, primaryCategory: 'CIVIL_MATERIALS',  hasContract: false, ytdChangeVsLastYear: 12.8  },
  { supplierId: 'sup-olex',       supplierName: 'Prysmian Group (Olex)',            abn: '67 091 231 772', totalExGST: 47_120,  invoiceCount: 5,  lastInvoiceDate: '2026-06-15', avgPaymentDays: 31, primaryCategory: 'POWER_CABLE',      hasContract: true,  ytdChangeVsLastYear: 19.4  },
  { supplierId: 'sup-saferoads',  supplierName: 'Saferoads Queensland',             abn: '89 124 603 271', totalExGST: 31_480,  invoiceCount: 6,  lastInvoiceDate: '2026-06-01', avgPaymentDays: 22, primaryCategory: 'CIVIL_MATERIALS',  hasContract: true,  ytdChangeVsLastYear: 8.1   },
  { supplierId: 'sup-downer',     supplierName: 'Downer EDI Works',                 abn: '75 096 278 992', totalExGST: 28_600,  invoiceCount: 2,  lastInvoiceDate: '2026-05-15', avgPaymentDays: 45, primaryCategory: 'SUBCONTRACT',      hasContract: true,  ytdChangeVsLastYear: 0.0   },
  { supplierId: 'sup-blackwoods', supplierName: 'Blackwoods Australia',             abn: '59 004 400 405', totalExGST: 12_840,  invoiceCount: 11, lastInvoiceDate: '2026-05-10', avgPaymentDays: 19, primaryCategory: 'PPE',              hasContract: false, ytdChangeVsLastYear: 5.8   },
  { supplierId: 'sup-puma',       supplierName: 'Puma Energy',                      abn: '63 088 572 415', totalExGST: 4_480,   invoiceCount: 4,  lastInvoiceDate: '2026-06-10', avgPaymentDays: 14, primaryCategory: 'FUEL',             hasContract: false, ytdChangeVsLastYear: -9.2  },
  { supplierId: 'sup-schneider',  supplierName: 'Schneider Electric',               abn: '21 004 851 162', totalExGST: 4_232,   invoiceCount: 1,  lastInvoiceDate: '2026-05-20', avgPaymentDays: 30, primaryCategory: 'SIGNAL_EQUIPMENT', hasContract: false, ytdChangeVsLastYear: 2.1   },
]

// ── Job spend breakdown ────────────────────────────────────────────

export const JOB_SPEND: JobSpend[] = [
  { jobId: 'j1', jobNumber: 'TP-2026-001', jobName: 'Sandgate Rd Signal Upgrade',          contractValue: 1_240_000, totalCommitted: 186_800, totalInvoiced: 57_892, totalPaid: 530_100, budgetUtilisationPct: 63, status: 'ON_BUDGET' },
  { jobId: 'j2', jobNumber: 'TP-2026-002', jobName: 'Bowen Hills Substation HV Connection', contractValue: 680_000,   totalCommitted: 98_400,  totalInvoiced: 28_400, totalPaid: 340_000, budgetUtilisationPct: 54, status: 'ON_BUDGET' },
  { jobId: 'j3', jobNumber: 'TP-2026-003', jobName: 'Maroochydore Rd Reconstruction',       contractValue: 2_100_000, totalCommitted: 512_800, totalInvoiced: 6_232,  totalPaid: 0,       budgetUtilisationPct: 24, status: 'ON_BUDGET' },
  { jobId: 'j4', jobNumber: 'TP-2026-004', jobName: 'Virginia ITS Node',                    contractValue: 290_000,   totalCommitted: 14_948,  totalInvoiced: 0,      totalPaid: 0,       budgetUtilisationPct: 5,  status: 'ON_BUDGET' },
]

// ── Summary KPIs ──────────────────────────────────────────────────

export const SPEND_KPIS: SpendKPI[] = [
  { label: 'YTD spend (ex GST)', value: '$282,792',  sub: 'Jan–Jun 2026',    trend: 28.4, color: 'text-[#1F3864]' },
  { label: 'Paid to suppliers',  value: '$203,548',  sub: 'invoices cleared', trend: 22.1, color: 'text-green-700' },
  { label: 'Awaiting payment',   value: '$79,244',   sub: 'approved, unpaid', trend: 8.2,  color: 'text-amber-600' },
  { label: 'Open PO commitment', value: '$813,100',  sub: 'approved POs',     trend: -4.1, color: 'text-[#2E75B6]' },
]

// ── Helpers ────────────────────────────────────────────────────────

export const TOTAL_YTD_SPEND = MONTHLY_SPEND.reduce((s, m) => s + m.invoiced, 0)
export const MAX_MONTHLY     = Math.max(...MONTHLY_SPEND.map(m => m.invoiced))
export const MAX_CATEGORY    = Math.max(...CATEGORY_SPEND.map(c => c.totalExGST))
export const MAX_SUPPLIER    = Math.max(...SUPPLIER_SPEND.map(s => s.totalExGST))
